import type { CSSObject } from "@emotion/react";

/* Exact Emotion equivalents of recurring compiled Tailwind patterns.
 *
 * The compiled utilities rely on `--tw-*` custom properties whose defaults come
 * from Tailwind's universal `*,::before,::after` block in the shipped
 * stylesheet. Migrated rules inline those defaults so each Emotion rule is
 * self-contained and survives the eventual disappearance of the universal
 * block. Values are byte-for-byte the compiled output (with the empty
 * `--tw-ring-inset` default substituted away). Do not tweak.
 */

/** `ring-<width>` + `ring-<color>`: focus ring drawn with box-shadow. */
export const twRing = (widthPx: string, color: string): CSSObject => ({
  "--tw-ring-offset-width": "0px",
  "--tw-ring-offset-color": "#fff",
  "--tw-ring-color": color,
  "--tw-ring-offset-shadow": "0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color)",
  "--tw-ring-shadow": `0 0 0 calc(${widthPx} + var(--tw-ring-offset-width)) var(--tw-ring-color)`,
  boxShadow: "var(--tw-ring-offset-shadow),var(--tw-ring-shadow),var(--tw-shadow, 0 0 #0000)"
});

/* Keyframes below are byte-for-byte the compiled output of the library's
 * Tailwind keyframes (the `bfrs-bfrs-*` names come from the `bfrs-` class
 * prefix applied to the already-`bfrs-`-named keyframes). They are emitted
 * from within each component's Emotion styles so the animation NAME in
 * computed styles stays identical to the baseline, and so the keyframes
 * exist inside shadow roots (keyframes do not cross shadow boundaries). */

export const kfContentIn: CSSObject = {
  "@keyframes bfrs-bfrs-content-in": {
    "0%": { opacity: 0, transform: "translateY(8px) scale(.98)" },
    to: { opacity: 1, transform: "translateY(0) scale(1)" }
  }
};

export const kfOverlayIn: CSSObject = {
  "@keyframes bfrs-bfrs-overlay-in": {
    "0%": { opacity: 0 },
    to: { opacity: 1 }
  }
};

export const kfModalContentIn: CSSObject = {
  "@keyframes bfrs-bfrs-modal-content-in": {
    "0%": { opacity: 0, transform: "translate(-50%,calc(-50% + 8px)) scale(.98)" },
    to: { opacity: 1, transform: "translate(-50%,-50%) scale(1)" }
  }
};

export const kfSpin: CSSObject = {
  "@keyframes bfrs-bfrs-spin": {
    to: { transform: "rotate(360deg)" }
  }
};

export const kfDrawerRightIn: CSSObject = {
  "@keyframes bfrs-bfrs-drawer-right-in": {
    "0%": { transform: "translate(100%)" },
    to: { transform: "translate(0)" }
  }
};

export const kfDrawerLeftIn: CSSObject = {
  "@keyframes bfrs-bfrs-drawer-left-in": {
    "0%": { transform: "translate(-100%)" },
    to: { transform: "translate(0)" }
  }
};

export const kfDrawerBottomIn: CSSObject = {
  "@keyframes bfrs-bfrs-drawer-bottom-in": {
    "0%": { transform: "translateY(100%)" },
    to: { transform: "translateY(0)" }
  }
};

export const kfShimmer: CSSObject = {
  "@keyframes bfrs-bfrs-shimmer": {
    to: { content: "var(--tw-content)", transform: "translate(100%)" }
  }
};

/** Tailwind's composed transform with inlined defaults; pass the overrides. */
export const twTransform = (overrides: Partial<Record<"--tw-translate-x" | "--tw-translate-y" | "--tw-rotate" | "--tw-skew-x" | "--tw-skew-y" | "--tw-scale-x" | "--tw-scale-y", string>>): CSSObject => ({
  "--tw-translate-x": "0",
  "--tw-translate-y": "0",
  "--tw-rotate": "0",
  "--tw-skew-x": "0",
  "--tw-skew-y": "0",
  "--tw-scale-x": "1",
  "--tw-scale-y": "1",
  ...overrides,
  transform:
    "translate(var(--tw-translate-x),var(--tw-translate-y)) rotate(var(--tw-rotate)) skew(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))"
});
